import { useCallback, useEffect, useMemo, useState } from "react";
import { Lightbulb, MessageSquarePlus, ThumbsUp } from "lucide-react";

import { apiFetch } from "@/lib/api-client";
import { formatDate, formatDateTime } from "@/lib/i18n";
import { NpsModal } from "@/components/nps-csat-modal";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

type FeatureRequestStatus = "open" | "planned" | "shipped";

interface NpsResponse {
  id: string;
  score: number;
  comment: string | null;
  context: string | null;
  submittedAt: string;
}

interface NpsSummary {
  shouldPrompt: boolean;
  daysSinceWorkspaceCreated: number;
  workspaceCreatedAt: string;
  response: NpsResponse | null;
}

interface FeatureRequest {
  id: string;
  tenantId: string;
  submittedByUserId: string;
  title: string;
  description: string;
  status: FeatureRequestStatus;
  voteCount: number;
  hasVoted: boolean;
  createdAt: string;
  updatedAt: string;
}

const STATUS_TONE: Record<FeatureRequestStatus, string> = {
  open: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  planned: "bg-[var(--color-warning)]/15 text-[var(--color-warning)]",
  shipped: "bg-[var(--color-success)]/15 text-[var(--color-success)]",
};

export default function FeedbackPage() {
  const [summary, setSummary] = useState<NpsSummary | null>(null);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNpsOpen, setIsNpsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summaryRes, requestsRes] = await Promise.all([
        apiFetch<{ data: NpsSummary }>("/v1/platform/feedback/nps"),
        apiFetch<{ data: { requests: FeatureRequest[] } }>("/v1/platform/feedback/requests"),
      ]);
      setSummary(summaryRes.data);
      setRequests(requestsRes.data.requests ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load feedback.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFeedback();
  }, [loadFeedback]);

  const statusCounts = useMemo(() => ({
    open: requests.filter((request) => request.status === "open").length,
    planned: requests.filter((request) => request.status === "planned").length,
    shipped: requests.filter((request) => request.status === "shipped").length,
  }), [requests]);

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiFetch<{ data: FeatureRequest }>("/v1/platform/feedback/requests", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
        }),
      });

      setRequests((current) => [res.data, ...current]);
      setTitle("");
      setDescription("");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit feature request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (requestId: string) => {
    try {
      const res = await apiFetch<{ data: { voteCount: number; hasVoted: boolean } }>(`/v1/platform/feedback/requests/${requestId}/vote`, {
        method: "POST",
      });

      setRequests((current) => current.map((request) => request.id === requestId
        ? { ...request, voteCount: res.data.voteCount, hasVoted: res.data.hasVoted }
        : request));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record vote.");
    }
  };

  const handleNpsClose = async () => {
    setIsNpsOpen(false);
    await loadFeedback();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <SkeletonCard className="h-44" />
        <SkeletonCard className="h-56" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-8">
      <div className="flex items-start gap-3">
        <Lightbulb className="mt-1 h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Feedback</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Share how the workspace is performing and upvote the product improvements that matter most.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-[var(--color-error)]/25 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Workspace NPS</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                We ask after the first 14 days of active workspace life, then store the response truthfully on the mounted platform seam.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsNpsOpen(true)}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              Leave feedback
            </button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl bg-[var(--color-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Workspace age</p>
              <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{summary?.daysSinceWorkspaceCreated ?? 0} days</p>
            </div>
            <div className="rounded-xl bg-[var(--color-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Prompt status</p>
              <p className="mt-2 text-sm font-medium text-[var(--color-foreground)]">
                {summary?.response ? "Submitted" : summary?.shouldPrompt ? "Ready now" : "Not due yet"}
              </p>
            </div>
            <div className="rounded-xl bg-[var(--color-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Latest score</p>
              <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
                {summary?.response ? `${summary.response.score}/10` : "None"}
              </p>
            </div>
          </div>

          {summary?.response ? (
            <div className="mt-4 rounded-xl border border-[var(--color-border)] px-4 py-3">
              <p className="text-sm font-medium text-[var(--color-foreground)]">Latest response</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Submitted {formatDateTime(summary.response.submittedAt)}
              </p>
              {summary.response.comment ? (
                <p className="mt-3 text-sm text-[var(--color-foreground)]">{summary.response.comment}</p>
              ) : (
                <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No written comment was included.</p>
              )}
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Roadmap snapshot</h2>
          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between rounded-xl bg-[var(--color-surface)] px-4 py-3">
              <span className="text-sm text-[var(--color-muted-foreground)]">Open</span>
              <span className="text-lg font-semibold text-[var(--color-foreground)]">{statusCounts.open}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[var(--color-surface)] px-4 py-3">
              <span className="text-sm text-[var(--color-muted-foreground)]">Planned</span>
              <span className="text-lg font-semibold text-[var(--color-foreground)]">{statusCounts.planned}</span>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[var(--color-surface)] px-4 py-3">
              <span className="text-sm text-[var(--color-muted-foreground)]">Shipped</span>
              <span className="text-lg font-semibold text-[var(--color-foreground)]">{statusCounts.shipped}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[0.95fr_1.35fr]">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-start gap-2">
            <MessageSquarePlus className="mt-0.5 h-4 w-4 text-[var(--color-primary)]" />
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Submit a feature request</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Request truth is global, but your tenant context is attached so product signals stay attributable.
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Title</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                placeholder="Short summary of the outcome you need"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Description</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={5}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                placeholder="Describe the job to be done, the current friction, and what good looks like."
              />
            </label>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isSubmitting || title.trim().length < 3 || description.trim().length < 10}
              className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : "Submit request"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Top requests</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Sorted by status and live vote count from the mounted platform seam.</p>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {requests.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)]">
                No requests yet. Add the first one from this workspace.
              </div>
            ) : requests.map((request) => (
              <article key={request.id} className="rounded-xl border border-[var(--color-border)] p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-[var(--color-foreground)]">{request.title}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONE[request.status]}`}>
                        {request.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{request.description}</p>
                    <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                      Submitted {formatDate(request.createdAt)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleVote(request.id)}
                    disabled={request.hasVoted}
                    className={`inline-flex min-w-[88px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
                      request.hasVoted
                        ? "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                        : "border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                    }`}
                  >
                    <ThumbsUp className="h-4 w-4" />
                    {request.voteCount}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <NpsModal
        isOpen={isNpsOpen}
        onClose={() => void handleNpsClose()}
        context="settings_feedback_page"
      />
    </div>
  );
}
